import React, { useState, useEffect } from 'react';
import { useUser } from '../../contextapi/UserContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PhoneInput } from '../ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { Save, User, Building, Briefcase, Mail } from 'lucide-react';

interface BasicProfileFormProps {
  onSave?: (profileData: ProfileFormData) => void;
  showSaveButton?: boolean;
}

interface ProfileFormData {
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  job_title: string;
  company: string;
  timezone: string;
}

export const BasicProfileForm: React.FC<BasicProfileFormProps> = ({
  onSave,
  showSaveButton = true
}) => {
  const { user } = useUser();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    last_name: '',
    full_name: '',
    phone: '',
    job_title: '',
    company: '',
    timezone: 'Australia/Sydney'
  });

  // Timezone options (same as ProfileCompletionStep - Australian timezones prioritized)
  const timezones = [
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }
  ];

  // Load existing profile data
  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user?.id]);

  const splitNameFromFullName = (fullName: string): { first: string; last: string } => {
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) {
      return { first: parts[0], last: '' };
    }
    const first = parts[0];
    const last = parts.slice(1).join(' ');
    return { first, last };
  };

  const loadProfileData = async () => {
    if (!user?.id) return;

    try {
      const { supabase } = await import('../../utils/supabaseClient');
      
      const { data: profileData, error } = await supabase
        .rpc('get_user_profile_enhanced', { user_id: user.id });

      if (!error && profileData && profileData.length > 0) {
        const profile = profileData[0];
        
        // If first_name and last_name are empty but full_name exists, split it
        let firstName = profile.first_name || '';
        let lastName = profile.last_name || '';
        
        if (!firstName && !lastName && profile.full_name) {
          const { first, last } = splitNameFromFullName(profile.full_name);
          firstName = first;
          lastName = last;
        }
        
        // If still empty, check if user has a display_name from auth (Google sign-in)
        if (!firstName && !lastName && user.user_metadata?.full_name) {
          const { first, last } = splitNameFromFullName(user.user_metadata.full_name);
          firstName = first;
          lastName = last;
        }
        
        // Ensure timezone uses saved value, or default if null/empty
        // Check if saved timezone exists in our timezones list, otherwise use default
        const savedTimezone = profile.timezone?.trim();
        const timezoneExists = savedTimezone && timezones.some(tz => tz.value === savedTimezone);
        const timezoneToUse = timezoneExists ? savedTimezone : 'Australia/Sydney';

        setFormData({
          first_name: firstName,
          last_name: lastName,
          full_name: profile.full_name || '',
          phone: profile.phone || '',
          job_title: profile.job_title || '',
          company: profile.company || '',
          timezone: timezoneToUse
        });
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    }
  };

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate full name from first and last name
    if (field === 'first_name' || field === 'last_name') {
      const firstName = field === 'first_name' ? value : formData.first_name;
      const lastName = field === 'last_name' ? value : formData.last_name;
      const fullName = `${firstName} ${lastName}`.trim();
      
      setFormData(prev => ({
        ...prev,
        full_name: fullName
      }));
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    // Australian phone number validation
    const cleanPhone = phone.replace(/\s/g, '');
    return /^(\+61|0)[2-9]\d{8}$/.test(cleanPhone);
  };

  const handleSave = async () => {
    if (!user?.id) return;

    // Validate phone number if provided
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      toast.toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid Australian phone number (e.g., +61 4XX XXX XXX)',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { supabase } = await import('../../utils/supabaseClient');

      // Ensure timezone has a default value if not provided
      const timezoneToSave = formData.timezone || 'Australia/Sydney';

      const profilePayload = {
        id: user.id,
        email: user.email || '',
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        full_name: formData.full_name || null,
        phone: formData.phone || null,
        job_title: formData.job_title || null,
        company: formData.company || null,
        timezone: timezoneToSave,
        updated_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
      };

      // Direct upsert to base_profiles (avoids buggy update_user_profile_enhanced RPC)
      const { error: upsertError } = await supabase
        .from('base_profiles')
        .upsert(profilePayload, { onConflict: 'id' });

      if (upsertError) {
        throw upsertError;
      }

      toast.toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
      });

      onSave?.(formData);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Basic Profile Information
        </CardTitle>
        <CardDescription>
          Update your personal and professional information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder="Enter your first name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder="Enter your last name"
            />
          </div>
        </div>

        {/* Email (read-only) */}
        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email
          </Label>
          <Input
            id="email"
            value={user?.email || ''}
            readOnly
            disabled
            className="bg-muted cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground">
            Email address cannot be changed
          </p>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <PhoneInput
            value={formData.phone}
            onChange={(value, validation) => {
              handleInputChange('phone', value);
            }}
            placeholder="Enter Australian phone number"
          />
          <p className="text-xs text-muted-foreground">
            Enter your Australian phone number (optional)
          </p>
        </div>

        {/* Professional Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="job_title" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job Title
            </Label>
            <Input
              id="job_title"
              value={formData.job_title}
              onChange={(e) => handleInputChange('job_title', e.target.value)}
              placeholder="e.g., Software Engineer"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Company
            </Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="e.g., Acme Corporation"
            />
          </div>
        </div>

        {/* Timezone */}
        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Select
            value={formData.timezone}
            onValueChange={(value) => handleInputChange('timezone', value)}
          >
            <SelectTrigger id="timezone">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Save Button */}
        {showSaveButton && (
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={loading}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
